/**
 * SubstrateBatchList Component
 *
 * List view for substrate batches with filtering, status management, and batch actions.
 * Part of US-P2-003: Substrate Production & Block Creation
 *
 * Features:
 * - Table: Batch Code | Recipe | Format | Blocks Created | Available | Status | Mixed Date | Actions
 * - Status filter buttons (6 statuses)
 * - Inline status transition buttons
 * - Delete action (only if not READY/DEPLETED)
 */

import { useState, useMemo } from 'react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import SearchInput from '@/components/ui/search-input'
import {
  useSubstrateBatches,
  useSubstrateInventory,
  useSubstrateMutations,
  SUBSTRATE_STATUSES
} from '@/hooks/useSubstrateBatches'
import { useRecipes } from '@/hooks/useSubstrateResources'
import SubstrateBatchForm from './SubstrateBatchForm'
import SubstrateBatchSimpleForm from './SubstrateBatchSimpleForm'
import SubstrateStatusBadge, { getNextStatus } from './SubstrateStatusBadge'
import SubstrateBatchDetailModal from './SubstrateBatchDetailModal'
import CountBagsModal from './CountBagsModal'
import EditRecipeModal from './EditRecipeModal'
import MoveToHeatCyclingDialog from './MoveToHeatCyclingDialog'
import RecipeBookModal from './RecipeBookModal'
import BlockFormatManager from './BlockFormatManager'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import {
  Plus,
  Package,
  AlertTriangle,
  Filter,
  MoreHorizontal,
  ChevronRight,
  Trash2,
  Clock,
  Boxes,
  Flame,
  BookOpen,
  Layers
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { parseDateString } from '@/utils/dateUtils'

// Status filter options
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'MIXING', label: 'Mixing' },
  { value: 'BAGGED', label: 'Bagged' },
  { value: 'HEAT_CYCLING', label: 'Heat Cycling' },
  { value: 'COOLING', label: 'Cooling' },
  { value: 'READY', label: 'Ready' },
  { value: 'DEPLETED', label: 'Depleted' }
]

export default function SubstrateBatchList({ user, onLogout }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [recipeFilter, setRecipeFilter] = useState('all')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingBatch, setEditingBatch] = useState(null)
  const [isHeatCycleFormOpen, setIsHeatCycleFormOpen] = useState(false)
  const [selectedBatchForHeatCycle, setSelectedBatchForHeatCycle] = useState(null)
  const [isRecipeBookOpen, setIsRecipeBookOpen] = useState(false)
  const [isBlockFormatManagerOpen, setIsBlockFormatManagerOpen] = useState(false)
  const [batchToDelete, setBatchToDelete] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [batchToCount, setBatchToCount] = useState(null)
  const [editingRecipeBatch, setEditingRecipeBatch] = useState(null)

  const { substrateBatches, loading, error, refetch } = useSubstrateBatches()
  const { inventory, loading: inventoryLoading } = useSubstrateInventory()
  const { recipes } = useRecipes()
  const {
    advanceStatus,
    canAdvanceStatus,
    markAsHeatCycling,
    deleteBatch,
    loading: mutationLoading
  } = useSubstrateMutations()

  // Filter and search logic
  const filteredBatches = useMemo(() => {
    let result = [...substrateBatches]

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(batch => batch.status === statusFilter)
    }

    // Recipe filter
    if (recipeFilter !== 'all') {
      result = result.filter(batch => batch.recipe_id === recipeFilter)
    }

    // Search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(batch =>
        batch.batch_code?.toLowerCase().includes(search) ||
        batch.recipe?.name?.toLowerCase().includes(search) ||
        batch.block_format?.name?.toLowerCase().includes(search)
      )
    }

    return result
  }, [substrateBatches, statusFilter, recipeFilter, searchTerm])

  const handleAddBatch = () => {
    setEditingBatch(null)
    setIsFormOpen(true)
  }

  const handleAdvanceStatus = async (batch) => {
    // Intercept the BAGGED → HEAT_CYCLING transition so we can capture an
    // optional barrel hint and stamp heat_cycle_started_at via the dialog.
    if (batch.status === 'BAGGED') {
      setSelectedBatchForHeatCycle(batch)
      setIsHeatCycleFormOpen(true)
      return
    }
    try {
      await advanceStatus(batch)
      refetch()
    } catch (error) {
      console.error('Failed to advance status:', error)
    }
  }

  const handleFormSave = async () => {
    setIsFormOpen(false)
    refetch()
  }

  const handleAssignToHeatCycle = (batch) => {
    setSelectedBatchForHeatCycle(batch)
    setIsHeatCycleFormOpen(true)
  }

  const handleHeatCycleFormSave = () => {
    setIsHeatCycleFormOpen(false)
    setSelectedBatchForHeatCycle(null)
    refetch()
  }

  const handleDeleteBatch = async () => {
    if (!batchToDelete) return

    setDeleteError(null)
    try {
      await deleteBatch(batchToDelete.id)
      setBatchToDelete(null)
      refetch()
    } catch (error) {
      console.error('Failed to delete batch:', error)
      setDeleteError(error.message)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    // mixed_at can be a full timestamp or YYYY-MM-DD; use parseDateString for date-only, Date constructor for timestamps
    const date = dateStr.includes('T') ? new Date(dateStr) : parseDateString(dateStr)
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const calculateCoolingHours = (cooledAt) => {
    if (!cooledAt) return null
    const cooledTime = new Date(cooledAt)
    const now = new Date()
    return ((now.getTime() - cooledTime.getTime()) / (1000 * 60 * 60)).toFixed(1)
  }

  if (loading) {
    return (
      <PageLayout
        title="Substrate Batches"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-content-secondary">Loading...</div>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout
        title="Substrate Batches"
        user={user}
        onLogout={onLogout}
      >
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading substrate batches: {error}
          </AlertDescription>
        </Alert>
      </PageLayout>
    )
  }

  // Header actions: search → filters → secondary actions → primary action
  const headerActions = (
    <>
      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search batches..."
        className="w-[180px]"
      />

      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-[140px]">
          <Filter className="h-4 w-4 mr-2" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={recipeFilter} onValueChange={setRecipeFilter}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="All Recipes" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Recipes</SelectItem>
          {recipes.map(recipe => (
            <SelectItem key={recipe.id} value={recipe.id}>
              {recipe.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button variant="outline" onClick={() => setIsRecipeBookOpen(true)}>
        <BookOpen className="h-4 w-4 mr-2" />
        Recipes
      </Button>

      <Button variant="outline" onClick={() => setIsBlockFormatManagerOpen(true)}>
        <Boxes className="h-4 w-4 mr-2" />
        Formats
      </Button>

      <Button onClick={handleAddBatch}>
        <Plus className="h-4 w-4 mr-2" />
        New Batch
      </Button>
    </>
  )

  return (
    <PageLayout
      title="Substrate"
      icon={Layers}
      user={user}
      onLogout={onLogout}
      headerActions={headerActions}
    >
      <div className="h-full overflow-y-auto">
      {/* Low stock warnings for READY blocks */}
      {inventory && inventory.ready_for_inoculation < 50 && (
        <Alert className="mb-6 border-amber-500 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-500">
            Low substrate inventory! Only {inventory.ready_for_inoculation} blocks ready for inoculation.
            Consider creating new substrate batches.
          </AlertDescription>
        </Alert>
      )}

      {/* Results count */}
      <div className="text-sm text-content-secondary mb-4">
        Showing {filteredBatches.length} of {substrateBatches.length} substrate batches
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch Code</TableHead>
              <TableHead>Recipe</TableHead>
              <TableHead>Format</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Available</TableHead>
              <TableHead>Mixed</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredBatches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-content-secondary">
                  {substrateBatches.length === 0 ? (
                    <div className="flex flex-col items-center gap-2">
                      <Package className="h-8 w-8 text-content-tertiary" />
                      <span>No substrate batches yet</span>
                      <Button variant="outline" size="sm" onClick={handleAddBatch}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create First Batch
                      </Button>
                    </div>
                  ) : (
                    'No batches match your filters'
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredBatches.map(batch => {
                const nextStatus = getNextStatus(batch.status)
                const advanceCheck = canAdvanceStatus(batch)
                const coolingHours = calculateCoolingHours(batch.cooled_at)

                return (
                  <TableRow key={batch.id}>
                    <TableCell className="font-mono font-medium">
                      <button
                        className="hover:underline hover:text-odin-blue cursor-pointer transition-colors"
                        onClick={() => setSelectedBatch(batch)}
                      >
                        {batch.batch_code}
                      </button>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{batch.recipe?.name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      {batch.fills?.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {batch.fills.map(fill => (
                            <div key={fill.id} className="flex items-center gap-1">
                              <Badge variant="outline" className="text-xs font-normal">
                                {fill.block_format?.name}
                              </Badge>
                              <span className="text-xs text-content-tertiary">
                                ({fill.available}/{fill.quantity})
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : batch.block_format?.name ? (
                        <span className="text-sm">{batch.block_format.name}</span>
                      ) : (
                        <span className="text-xs text-content-tertiary italic">pending count</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {batch.total_blocks ?? <span className="text-content-tertiary">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {batch.available_blocks == null ? (
                        <span className="text-content-tertiary">—</span>
                      ) : (
                        <span className={batch.available_blocks > 0 ? 'text-odin-green font-medium' : 'text-content-tertiary'}>
                          {batch.available_blocks}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{formatDate(batch.mixed_at)}</span>
                        {batch.status === 'COOLING' && coolingHours && (
                          <span className="text-xs text-content-tertiary flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {coolingHours}h cooling
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <SubstrateStatusBadge status={batch.status} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" disabled={mutationLoading}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          {/* Count Bags — when count not yet recorded */}
                          {batch.total_blocks == null && batch.status !== 'DEPLETED' && (
                            <>
                              <DropdownMenuItem onClick={() => setBatchToCount(batch)}>
                                <Boxes className="h-4 w-4 mr-2 text-odin-blue" />
                                Count Bags (trolley wheeled in)
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}

                          {/* Advance Status Action */}
                          {nextStatus && batch.status !== 'DEPLETED' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => advanceCheck.canAdvance && handleAdvanceStatus(batch)}
                                disabled={!advanceCheck.canAdvance}
                                className={!advanceCheck.canAdvance ? 'opacity-50' : ''}
                              >
                                <ChevronRight className="h-4 w-4 mr-2 text-odin-blue" />
                                {advanceCheck.canAdvance ? (
                                  `Move to ${nextStatus.replace('_', ' ')}`
                                ) : (
                                  <span className="text-xs">{advanceCheck.reason}</span>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}

                          {/* Assign to Heat Cycle (only for BAGGED) */}
                          {batch.status === 'BAGGED' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleAssignToHeatCycle(batch)}
                              >
                                <Flame className="h-4 w-4 mr-2 text-orange-600" />
                                Assign to Heat Cycle
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}

                          {/* Edit (only for MIXING/BAGGED with a known count — otherwise use Count Bags) */}
                          {['MIXING', 'BAGGED'].includes(batch.status) && batch.total_blocks != null && (
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingBatch(batch)
                                setIsFormOpen(true)
                              }}
                            >
                              Edit Details
                            </DropdownMenuItem>
                          )}

                          {/* Edit Recipe (adjust ingredient quantities for this batch) */}
                          {['MIXING', 'BAGGED'].includes(batch.status) && (
                            <DropdownMenuItem onClick={() => setEditingRecipeBatch(batch)}>
                              <BookOpen className="h-4 w-4 mr-2 text-odin-blue" />
                              Edit Recipe
                            </DropdownMenuItem>
                          )}

                          {/* Delete (only if not READY/DEPLETED) */}
                          {!['READY', 'DEPLETED'].includes(batch.status) && (
                            <DropdownMenuItem
                              className="text-odin-red"
                              onClick={() => setBatchToDelete(batch)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Batch
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </Card>
      </div>

      {/* Substrate Batch Form Dialog - Simplified form for new batches */}
      {isFormOpen && !editingBatch && (
        <SubstrateBatchSimpleForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSave={handleFormSave}
        />
      )}

      {/* Count Bags Modal */}
      {batchToCount && (
        <CountBagsModal
          isOpen={!!batchToCount}
          batch={batchToCount}
          onClose={() => setBatchToCount(null)}
          onSave={() => {
            setBatchToCount(null)
            refetch()
          }}
        />
      )}

      {/* Edit Recipe Modal */}
      {editingRecipeBatch && (
        <EditRecipeModal
          isOpen={!!editingRecipeBatch}
          batch={editingRecipeBatch}
          onClose={() => setEditingRecipeBatch(null)}
          onSaved={() => {
            setEditingRecipeBatch(null)
            refetch()
          }}
        />
      )}

      {/* Substrate Batch Form Dialog - Full form for editing existing batches */}
      {isFormOpen && editingBatch && (
        <SubstrateBatchForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSave={handleFormSave}
          editingBatch={editingBatch}
        />
      )}

      {/* Move-to-HEAT_CYCLING dialog: stamps timestamp + optional barrel hint */}
      {isHeatCycleFormOpen && selectedBatchForHeatCycle && (
        <MoveToHeatCyclingDialog
          isOpen={isHeatCycleFormOpen}
          onClose={() => {
            setIsHeatCycleFormOpen(false)
            setSelectedBatchForHeatCycle(null)
          }}
          batch={selectedBatchForHeatCycle}
          onConfirm={async ({ barrelId }) => {
            await markAsHeatCycling(selectedBatchForHeatCycle.id, { barrelId })
            handleHeatCycleFormSave()
          }}
        />
      )}

      {/* Recipe Book Modal */}
      <RecipeBookModal
        open={isRecipeBookOpen}
        onOpenChange={setIsRecipeBookOpen}
      />

      {/* Block Format Manager Modal */}
      <Dialog open={isBlockFormatManagerOpen} onOpenChange={setIsBlockFormatManagerOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto">
          <BlockFormatManager />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!batchToDelete} onOpenChange={(open) => !open && setBatchToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Substrate Batch</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete batch <span className="font-mono font-medium">{batchToDelete?.batch_code}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}

          <div className="text-sm text-content-secondary">
            <p><strong>Status:</strong> {batchToDelete?.status}</p>
            <p><strong>Total blocks:</strong> {batchToDelete?.total_blocks ?? 'pending count'}</p>
            <p><strong>Recipe:</strong> {batchToDelete?.recipe?.name || '-'}</p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBatchToDelete(null)}
              disabled={mutationLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteBatch}
              disabled={mutationLoading}
            >
              {mutationLoading ? 'Deleting...' : 'Delete Batch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Batch Detail Modal */}
      <SubstrateBatchDetailModal
        isOpen={!!selectedBatch}
        onClose={() => setSelectedBatch(null)}
        batch={selectedBatch}
      />
    </PageLayout>
  )
}
